import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function ConfigPagos() {
  const { usuario } = useAuth();
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  if (usuario && usuario.rol !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const cargar = async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/config-pagos');
      setConfig(data || {});
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error al cargar' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const setC = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const subirQR = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert('El QR no debe superar 500 KB');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setC('qr_base64', reader.result);
    reader.readAsDataURL(file);
  };

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    try {
      await api.put('/config-pagos', config);
      setMensaje({ tipo: 'ok', texto: '✅ Configuración guardada' });
      setTimeout(() => setMensaje(null), 2500);
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al guardar' });
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="p-6 text-center text-gray-500">Cargando...</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-700">Configuración de pagos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Datos que verán los clientes para renovar su plan.
          </p>
        </div>
        <button
          onClick={cargar}
          className="px-4 py-2 border border-slate-400 rounded-lg hover:bg-slate-100 text-sm"
        >
          🔄 Actualizar
        </button>
      </div>

      {mensaje && (
        <div className={`mb-4 p-3 rounded-lg ${mensaje.tipo === 'ok' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {mensaje.texto}
        </div>
      )}

      <div className="space-y-6">
        {/* QR DeUna */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <h2 className="text-lg font-semibold text-slate-700 mb-1">
            📱 QR DeUna / Pichincha
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            Sube la imagen del QR de tu cuenta DeUna o Pichincha. Aparecerá en la pantalla de pago del cliente.
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={subirQR}
            className="block w-full px-3 py-2 border rounded-lg text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-100"
          />
          {config?.qr_base64 && (
            <div className="mt-4 flex flex-col items-center">
              <img
                src={config.qr_base64}
                alt="QR preview"
                style={{ maxWidth: 220 }}
                className="border rounded-lg p-2 bg-white"
              />
              <button
                onClick={() => setC('qr_base64', null)}
                className="mt-2 text-xs text-red-600 hover:underline"
              >
                ✕ Quitar QR
              </button>
            </div>
          )}
        </div>

        {/* Cuenta bancaria */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <h2 className="text-lg font-semibold text-slate-700 mb-4">
            🏦 Cuenta bancaria
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Banco</span>
              <input
                type="text"
                value={config?.banco || ''}
                onChange={(e) => setC('banco', e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Tipo de cuenta</span>
              <select
                value={config?.tipo_cuenta || 'Ahorros'}
                onChange={(e) => setC('tipo_cuenta', e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="Ahorros">Ahorros</option>
                <option value="Corriente">Corriente</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Nº de cuenta</span>
              <input
                type="text"
                value={config?.cuenta_pichincha || ''}
                onChange={(e) => setC('cuenta_pichincha', e.target.value)}
                placeholder="Ej: 2200123456"
                className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-mono"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Titular</span>
              <input
                type="text"
                value={config?.titular || ''}
                onChange={(e) => setC('titular', e.target.value)}
                placeholder="Nombre del titular"
                className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-700">CI / RUC del titular</span>
              <input
                type="text"
                value={config?.identificacion || ''}
                onChange={(e) => setC('identificacion', e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              />
            </label>
          </div>
        </div>

        {/* PayPal */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <h2 className="text-lg font-semibold text-slate-700 mb-4">
            🌎 PayPal (opcional)
          </h2>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Link de PayPal.me o botón de pago</span>
            <input
              type="url"
              value={config?.paypal_link || ''}
              onChange={(e) => setC('paypal_link', e.target.value)}
              placeholder="https://paypal.me/lluchunga"
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </label>
        </div>

        {/* Instrucciones */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <h2 className="text-lg font-semibold text-slate-700 mb-4">
            📝 Instrucciones adicionales
          </h2>
          <textarea
            value={config?.instrucciones || ''}
            onChange={(e) => setC('instrucciones', e.target.value)}
            rows={4}
            placeholder="Ej: Envía el comprobante al WhatsApp +593 98 156 0147 con tu nombre y RUC."
            className="w-full px-3 py-2 border rounded-lg text-sm"
          />
        </div>

        {/* Guardar */}
        <div className="flex justify-end gap-2">
          <button
            onClick={cargar}
            disabled={guardando}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
          >
            Restaurar
          </button>
          <button
            onClick={guardar}
            disabled={guardando}
            className="px-6 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            {guardando ? 'Guardando...' : '💾 Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}